import { useCallback, useEffect, useState } from "react";

// Custom event for broadcasting block status changes across all instances
const BLOCK_STATUS_CHANGE_EVENT = "brandBlockStatusChanged";

/**
 * Dispatch a custom event to notify all components about block status change
 * @param {number|string} brandId - The brand ID that was blocked/unblocked
 * @param {boolean} isBlocked - The new block status
 */
function broadcastBlockStatusChange(brandId, isBlocked) {
  const event = new CustomEvent(BLOCK_STATUS_CHANGE_EVENT, {
    detail: { brandId: Number(brandId), isBlocked },
  });
  window.dispatchEvent(event);
}

/**
 * @typedef {Object} BrandBlockStatusReturn
 * @property {boolean} isBlocked - Whether the brand is blocked
 * @property {boolean} loading - Loading state for block status operations
 * @property {string|null} error - Error message if any operation fails
 * @property {Function} checkBlockStatus - Function to re-check the block status
 * @property {Function} toggleBlock - Function to toggle block status (block/unblock)
 * @property {Function} blockBrand - Function to block the brand
 * @property {Function} unblockBrand - Function to unblock the brand
 * @property {Function} setBlockStatus - Function to manually set block status
 * @property {boolean} canBlock - Whether the current brand can block the target brand (not self)
 */

/**
 * Custom hook to manage brand block status for legacy app
 * @param {number|string} targetBrandId - The ID of the brand to check block status for
 * @returns {BrandBlockStatusReturn} - Object containing block status, loading state, and helper functions
 */
export function useBrandBlockStatus(targetBrandId) {
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentBrandId = store.getState().currentBrand.id;

  /**
   * Check if the target brand is blocked
   */
  const checkBlockStatus = useCallback(async () => {
    // Don't check if no brand ID or checking own brand
    if (!targetBrandId || targetBrandId === currentBrandId) {
      setIsBlocked(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await $.get(`/api/brands/${targetBrandId}/blocked`);
      setIsBlocked(response.isBlocked);
    } catch (err) {
      console.error("Failed to check block status:", err);
      setError(err.message || "Failed to check block status");
      setIsBlocked(false);
    } finally {
      setLoading(false);
    }
  }, [targetBrandId, currentBrandId]);

  /**
   * Toggle block status (block/unblock)
   * @param {Function} onSuccess - Optional callback to execute on successful toggle
   * @param {Function} onError - Optional callback to execute on error
   */
  const toggleBlock = useCallback(
    async (onSuccess, onError) => {
      if (!targetBrandId || targetBrandId === currentBrandId) {
        console.warn("Cannot block yourself or invalid brand ID");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let response;
        const newBlockStatus = !isBlocked;

        if (isBlocked) {
          // Unblock
          response = await $.ajax({
            url: `/api/brands/${targetBrandId}/block`,
            method: "DELETE",
          });
        } else {
          // Block
          response = await $.post(`/api/brands/${targetBrandId}/block`);
        }

        if (response.success) {
          setIsBlocked(newBlockStatus);
          // Broadcast the change to all other instances
          broadcastBlockStatusChange(targetBrandId, newBlockStatus);
          onSuccess?.(newBlockStatus);
        }
      } catch (err) {
        console.error("Failed to toggle block:", err);
        const errorMessage = err.message || "Failed to toggle block status";
        setError(errorMessage);
        onError?.(err);
      } finally {
        setLoading(false);
      }
    },
    [targetBrandId, currentBrandId, isBlocked],
  );

  /**
   * Block the brand
   * @param {Function} onSuccess - Optional callback to execute on successful block
   * @param {Function} onError - Optional callback to execute on error
   */
  const blockBrand = useCallback(
    async (onSuccess, onError) => {
      if (isBlocked) {
        console.warn("Brand is already blocked");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await $.post(`/api/brands/${targetBrandId}/block`);

        if (response.success) {
          setIsBlocked(true);
          // Broadcast the change to all other instances
          broadcastBlockStatusChange(targetBrandId, true);
          onSuccess?.();
        }
      } catch (err) {
        console.error("Failed to block brand:", err);
        const errorMessage = err.message || "Failed to block brand";
        setError(errorMessage);
        onError?.(err);
      } finally {
        setLoading(false);
      }
    },
    [targetBrandId, isBlocked],
  );

  /**
   * Unblock the brand
   * @param {Function} onSuccess - Optional callback to execute on successful unblock
   * @param {Function} onError - Optional callback to execute on error
   */
  const unblockBrand = useCallback(
    async (onSuccess, onError) => {
      if (!isBlocked) {
        console.warn("Brand is not blocked");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await $.ajax({
          url: `/api/brands/${targetBrandId}/block`,
          method: "DELETE",
        });

        if (response.success) {
          setIsBlocked(false);
          // Broadcast the change to all other instances
          broadcastBlockStatusChange(targetBrandId, false);
          onSuccess?.();
        }
      } catch (err) {
        console.error("Failed to unblock brand:", err);
        const errorMessage = err.message || "Failed to unblock brand";
        setError(errorMessage);
        onError?.(err);
      } finally {
        setLoading(false);
      }
    },
    [targetBrandId, isBlocked],
  );

  /**
   * Manually update block status (useful for parent components)
   */
  const setBlockStatus = useCallback((blocked) => {
    setIsBlocked(blocked);
  }, []);

  // Check block status on mount and when targetBrandId changes
  useEffect(() => {
    checkBlockStatus();
  }, [checkBlockStatus]);

  // Listen for block status changes from other instances
  useEffect(() => {
    const handleBlockStatusChange = (event) => {
      const { brandId, isBlocked: newBlockStatus } = event.detail;
      // Only update if this instance is tracking the same brand
      if (Number(brandId) === Number(targetBrandId)) {
        setIsBlocked(newBlockStatus);
      }
    };

    window.addEventListener(BLOCK_STATUS_CHANGE_EVENT, handleBlockStatusChange);

    return () => {
      window.removeEventListener(BLOCK_STATUS_CHANGE_EVENT, handleBlockStatusChange);
    };
  }, [targetBrandId]);

  return {
    isBlocked,
    loading,
    error,
    checkBlockStatus,
    toggleBlock,
    blockBrand,
    unblockBrand,
    setBlockStatus,
    canBlock: targetBrandId && targetBrandId !== currentBrandId,
  };
}
